import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Stack,
} from '@mui/material';
import { PaxPicker, SearchSelect } from '@travelity/form';
import type { PaxData } from '@travelity/api';
import { useEffect, useMemo, useRef } from 'react';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import type {
    PackageBookingFormValues,
    PackageBookingItemMeta,
} from '../../package-form.helpers';

const paxFields = [
    'adults',
    'children',
    'infants',
    'youth',
    'seniors',
] as const;

const clampPax = (next: PaxData = {}, max: PaxData = {}): PaxData =>
    paxFields.reduce<PaxData>((acc, field) => {
        const nextValue = next[field] ?? 0;
        const maxValue = max[field] ?? 0;
        const clampedValue = Math.max(0, Math.min(nextValue, maxValue));

        if (clampedValue) {
            acc[field] = clampedValue;
        }

        return acc;
    }, {});

const isSamePax = (left: PaxData = {}, right: PaxData = {}) =>
    paxFields.every((field) => (left[field] ?? 0) === (right[field] ?? 0));

interface PackageProductOptionDialogValues {
    selectedItemKey: string;
    selectedOptionId: string;
    draftPax: PaxData;
}

export interface PackageSelectedProductOptionCard {
    id: string;
    itemKey: string;
    optionId: string;
    productName: string;
    productOptionType?: string;
    productOptionName: string;
    pricing?: PackageBookingItemMeta['optionsCatalog'][number]['pricing'];
    pax: PackageBookingFormValues['pax'];
}

interface PackageProductAddOptionsDialogProps {
    open: boolean;
    selectedPax: PackageBookingFormValues['pax'];
    availableProducts: PackageBookingItemMeta[];
    availableProductByKey: Record<string, PackageBookingItemMeta>;
    selectedProductOptionsByItemKey: PackageBookingFormValues['selectedOptionsByItemKey'];
    editingProductOption?: PackageSelectedProductOptionCard | null;
    onClose: () => void;
    onConfirm: (card: PackageSelectedProductOptionCard) => void;
}

const PackageProductAddOptionsDialog = ({
    open,
    selectedPax,
    availableProducts,
    availableProductByKey,
    selectedProductOptionsByItemKey,
    editingProductOption,
    onClose,
    onConfirm,
}: PackageProductAddOptionsDialogProps) => {
    const methods = useForm<PackageProductOptionDialogValues>({
        mode: 'onChange',
        defaultValues: {
            selectedItemKey: '',
            selectedOptionId: '',
            draftPax: {},
        },
    });

    const selectedItemKey = useWatch({
        control: methods.control,
        name: 'selectedItemKey',
    });
    const selectedOptionId = useWatch({
        control: methods.control,
        name: 'selectedOptionId',
    });
    const watchedDraftPax = useWatch({
        control: methods.control,
        name: 'draftPax',
    });
    const previousSelectedItemKeyRef = useRef(selectedItemKey);

    const draftPax = useMemo(
        () => clampPax(watchedDraftPax || {}, selectedPax),
        [selectedPax, watchedDraftPax]
    );

    useEffect(() => {
        if (!open) return;

        methods.reset({
            selectedItemKey: editingProductOption?.itemKey || '',
            selectedOptionId: editingProductOption?.optionId || '',
            draftPax: clampPax(
                editingProductOption?.pax || selectedPax,
                selectedPax
            ),
        });
        previousSelectedItemKeyRef.current =
            editingProductOption?.itemKey || '';
    }, [editingProductOption, methods, open, selectedPax]);

    useEffect(() => {
        if (previousSelectedItemKeyRef.current !== selectedItemKey) {
            methods.setValue('selectedOptionId', '');
            previousSelectedItemKeyRef.current = selectedItemKey;
        }

        if (!selectedItemKey) return;

        const selectedProduct = availableProductByKey[selectedItemKey];
        if (!selectedProduct) {
            methods.setValue('selectedItemKey', '');
            methods.setValue('selectedOptionId', '');
            return;
        }

        const hasSelectedOption = selectedProduct.optionsCatalog.some(
            (option) => option.id === selectedOptionId
        );
        if (!hasSelectedOption) {
            methods.setValue('selectedOptionId', '');
        }
    }, [availableProductByKey, methods, selectedItemKey, selectedOptionId]);

    useEffect(() => {
        if (isSamePax(watchedDraftPax || {}, draftPax)) return;

        methods.setValue('draftPax', draftPax, {
            shouldDirty: false,
            shouldTouch: false,
            shouldValidate: false,
        });
    }, [draftPax, methods, watchedDraftPax]);

    const productOptions = useMemo(
        () =>
            availableProducts.map((product) => ({
                value: product.key,
                label: product.product.name,
            })),
        [availableProducts]
    );

    const selectedProduct = selectedItemKey
        ? availableProductByKey[selectedItemKey]
        : undefined;

    const productOptionChoices = useMemo(() => {
        const selectedProductOptions = selectedProduct?.optionsCatalog || [];
        const usedOptionIds = selectedItemKey
            ? (selectedProductOptionsByItemKey[selectedItemKey] || []).map(
                  (selectedProductOption) => selectedProductOption.optionId
              )
            : [];

        return selectedProductOptions
            .filter(
                (productOption) =>
                    productOption.id === editingProductOption?.optionId ||
                    !usedOptionIds.includes(productOption.id)
            )
            .map((productOption) => ({
                value: productOption.id,
                label: productOption.name,
            }));
    }, [
        editingProductOption?.optionId,
        selectedItemKey,
        selectedProduct,
        selectedProductOptionsByItemKey,
    ]);

    const handleConfirm = () => {
        if (!selectedItemKey || !selectedOptionId) return;

        const selectedProductMeta = availableProductByKey[selectedItemKey];
        const selectedProductOption = selectedProductMeta?.optionsCatalog.find(
            (productOption) => productOption.id === selectedOptionId
        );

        if (!selectedProductMeta || !selectedProductOption) return;

        onConfirm({
            id: `${selectedItemKey}:${selectedOptionId}`,
            itemKey: selectedItemKey,
            optionId: selectedOptionId,
            productName: selectedProductMeta.product.name,
            productOptionName: selectedProductOption.name,
            pricing: selectedProductOption.pricing,
            pax: clampPax(draftPax, selectedPax),
        });
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullWidth
            maxWidth={false}
            sx={{
                '& .MuiDialog-container': {
                    justifyContent: { xs: 'center', sm: 'flex-end' },
                    alignItems: 'center',
                },
            }}
            PaperProps={{
                sx: {
                    borderRadius: 0.5,
                    width: { xs: 'calc(100% - 32px)', sm: '388px' },
                    maxWidth: '388px',
                    m: 0,
                    mr: { xs: 0, sm: '32px' },
                },
            }}
        >
            <DialogTitle
                sx={{
                    fontSize: '18px',
                    fontWeight: 600,
                    color: 'text.primary',
                }}
            >
                Select Product Option
            </DialogTitle>
            <DialogContent>
                <FormProvider {...methods}>
                    <Stack gap={2} sx={{ py: 1 }}>
                        <SearchSelect
                            name="selectedItemKey"
                            label="Product"
                            placeholder="Select Product"
                            width="100%"
                            options={productOptions}
                        />
                        <SearchSelect
                            name="selectedOptionId"
                            label="Product option"
                            placeholder="Select Product Option"
                            width="100%"
                            options={productOptionChoices}
                            disabled={!selectedProduct}
                        />
                        <PaxPicker
                            name="draftPax"
                            withDropdown
                            maxPax={selectedPax}
                            selectProps={{
                                label: 'PAX',
                                width: '100%',
                                placeholder: 'Select PAX',
                            }}
                        />
                    </Stack>
                </FormProvider>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 3 }}>
                <Button color="neutral" variant="contained" onClick={onClose}>
                    Discard
                </Button>
                <Button
                    color="secondary"
                    variant="contained"
                    onClick={handleConfirm}
                    disabled={!selectedItemKey || !selectedOptionId}
                >
                    Add Option
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export { PackageProductAddOptionsDialog };
